import { type I18nLocale, t, Trans } from '@ardoq/i18n';
import { pluralize } from '@ardoq/common-helpers';
import { type Locale } from '@ardoq/locale';
import {
  getDateValueLabel,
  getDefaultTableFilterOperatorLabels,
  type DashboardPillLabels,
  type TableDisplaySelectorLabels,
  type TableFilterBarLabels,
  type TableFilterSelectorLabels,
} from '@ardoq/table-filter-bar';
import { type ColumnSelectMenuLabels } from '@ardoq/dropdown-2026';
import { InlineText, Text } from '@ardoq/typography';

export const getDashboardPillLabels = (
  i18nLocale: I18nLocale
): DashboardPillLabels => ({
  linkChartsButton: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.linkChartsButton',
    'Link charts'
  ),
  linkChartsHint: (
    <Text variant="text2">
      <Trans
        i18nKey="tableFilterBar:dashboardPill.linkChartsHint"
        defaults="First, use <bold>Link charts</bold> to pick the charts to filter. Dashboard filters only affect linked charts."
        components={{
          bold: <InlineText variant="text2Bold">{''}</InlineText>,
        }}
      />
    </Text>
  ),
  closeButton: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.closeButton',
    'Close'
  ),
  editLinkedCharts: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.editLinkedCharts',
    'Edit linked charts'
  ),
  renameFilter: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.renameFilter',
    'Rename filter'
  ),
  deleteFilter: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.deleteFilter',
    'Delete filter'
  ),
  renameDialogTitle: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.renameDialogTitle',
    'Rename dashboard filter'
  ),
  filterNameLabel: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.filterNameLabel',
    'Filter name'
  ),
  filterNameHelperText: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.filterNameHelperText',
    'Give your filter a clear, descriptive label'
  ),
  saveButton: t(i18nLocale, 'tableFilterBar:dashboardPill.saveButton', 'Save'),
  cancelButton: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.cancelButton',
    'Cancel'
  ),
  filterInputPlaceholder: t(
    i18nLocale,
    'tableFilterBar:dashboardPill.filterInputPlaceholder',
    'Search...'
  ),
  settingsAriaLabel: (filterLabel: string) =>
    t(
      i18nLocale,
      'tableFilterBar:dashboardPill.settingsAriaLabel',
      { filterLabel },
      `Edit ${filterLabel} filter settings`
    ),
});

export const getTableDisplaySelectorLabels = (
  i18nLocale: I18nLocale
): TableDisplaySelectorLabels => ({
  buttonLabel: t(
    i18nLocale,
    'tableFilterBar:displaySelector.buttonLabel',
    'Display'
  ),
  buttonAriaLabel: t(
    i18nLocale,
    'tableFilterBar:displaySelector.buttonAriaLabel',
    'Display settings'
  ),
});

export const getColumnSelectMenuLabels = (
  i18nLocale: I18nLocale
): ColumnSelectMenuLabels => ({
  visibilityHeader: t(
    i18nLocale,
    'tableFilterBar:columnSelect.visibilityHeader',
    'COLUMN VISIBILITY'
  ),
  chooseColumnsHeader: t(
    i18nLocale,
    'tableFilterBar:columnSelect.chooseColumnsHeader',
    'CHOOSE COLUMNS'
  ),
  searchPlaceholder: t(
    i18nLocale,
    'tableFilterBar:columnSelect.searchPlaceholder',
    'Filter columns'
  ),
  shownInTableHeader: t(
    i18nLocale,
    'tableFilterBar:columnSelect.shownInTableHeader',
    'Shown in table'
  ),
  hideAllAction: t(
    i18nLocale,
    'tableFilterBar:columnSelect.hideAllAction',
    'Hide all'
  ),
  hiddenInTableHeader: t(
    i18nLocale,
    'tableFilterBar:columnSelect.hiddenInTableHeader',
    'Hidden in table'
  ),
  showAllAction: t(
    i18nLocale,
    'tableFilterBar:columnSelect.showAllAction',
    'Show all'
  ),
  hideColumnAriaLabel: (columnLabel: string) =>
    t(
      i18nLocale,
      'tableFilterBar:columnSelect.hideColumnAriaLabel',
      { columnLabel },
      `Hide ${columnLabel} column`
    ),
  showColumnAriaLabel: (columnLabel: string) =>
    t(
      i18nLocale,
      'tableFilterBar:columnSelect.showColumnAriaLabel',
      { columnLabel },
      `Show ${columnLabel} column`
    ),
});

export const getTableFilterSelectorLabels = (
  i18nLocale: I18nLocale
): TableFilterSelectorLabels => ({
  buttonLabel: t(
    i18nLocale,
    'tableFilterBar:filterSelector.buttonLabel',
    'Filter'
  ),
  addFilterAriaLabel: t(
    i18nLocale,
    'tableFilterBar:filterSelector.addFilterAriaLabel',
    'Add filter'
  ),
  submenuAriaLabel: (filterLabel: string) =>
    t(
      i18nLocale,
      'tableFilterBar:filterSelector.submenuAriaLabel',
      { filterLabel },
      `Submenu for ${filterLabel}`
    ),
});

/**
 * Translated label set passed to the @ardoq/table-filter-bar package
 * (which is intentionally translation-free / Pattern A).
 *
 * Keys live in `tableFilterBar:` namespace; English defaults match the
 * package's defaults so callers in English locales get identical output.
 */
export const getTableFilterBarLabels = (
  i18nLocale: I18nLocale,
  locale: Locale
): TableFilterBarLabels => ({
  clearButtonLabel: t(i18nLocale, 'tableFilterBar:clearButtonLabel', 'Clear'),
  filterSelector: getTableFilterSelectorLabels(i18nLocale),
  displaySelector: getTableDisplaySelectorLabels(i18nLocale),
  pill: {
    removeFilterAriaLabel: t(
      i18nLocale,
      'tableFilterBar:pill.removeFilterAriaLabel',
      'Remove filter'
    ),
    editValueAriaLabel: (filterLabel: string) =>
      t(
        i18nLocale,
        'tableFilterBar:pill.editValueAriaLabel',
        { filterLabel },
        `Edit ${filterLabel} filter value`
      ),
    listMultiSelectionLabel: (count: number, lowerCaseFilterLabel: string) =>
      t(
        i18nLocale,
        'tableFilterBar:pill.listMultiSelectionLabel',
        { count, lowerCaseFilterLabel },
        `${count} ${pluralize(lowerCaseFilterLabel, count)}`
      ),
    dateValueLabel: getDateValueLabel(i18nLocale, locale),
    rangeLabel: (start: string, end: string) =>
      t(
        i18nLocale,
        'tableFilterBar:pill.rangeLabel',
        { start, end },
        `${start} – ${end}`
      ),
  },
  popover: {
    stringFilterLabel: (lowerCaseFilterLabel: string) =>
      t(
        i18nLocale,
        'tableFilterBar:popover.stringFilterLabel',
        { lowerCaseFilterLabel },
        `Filter by ${lowerCaseFilterLabel}`
      ),
    stringSubmitLabel: t(
      i18nLocale,
      'tableFilterBar:popover.stringSubmitLabel',
      'Add filter'
    ),
    numberSubmitLabel: t(
      i18nLocale,
      'tableFilterBar:popover.numberSubmitLabel',
      'Apply'
    ),
    datePlaceholder: t(
      i18nLocale,
      'tableFilterBar:popover.datePlaceholder',
      'Select a date'
    ),
    datePickerI18nLocale: i18nLocale,
    loadingLabel: t(
      i18nLocale,
      'tableFilterBar:popover.loadingLabel',
      'Loading...'
    ),
    noResultsLabel: t(
      i18nLocale,
      'tableFilterBar:popover.noResultsLabel',
      'No results'
    ),
    itemCountLabel: (count: number) =>
      t(
        i18nLocale,
        count === 1
          ? 'tableFilterBar:popover.itemCountLabelOne'
          : 'tableFilterBar:popover.itemCountLabelOther',
        { count },
        `${count} ${count === 1 ? 'item' : 'items'}`
      ),
    numberRange: {
      fromPrefix: t(
        i18nLocale,
        'tableFilterBar:popover.numberRange.fromPrefix',
        'From'
      ),
      toPrefix: t(
        i18nLocale,
        'tableFilterBar:popover.numberRange.toPrefix',
        'To'
      ),
      placeholder: t(
        i18nLocale,
        'tableFilterBar:popover.numberRange.placeholder',
        'Search...'
      ),
      startHigherError: t(
        i18nLocale,
        'tableFilterBar:popover.numberRange.startHigherError',
        "The 'From' value must be less than the 'To' value. Enter a valid range."
      ),
    },
    dateRange: {
      startLabel: t(
        i18nLocale,
        'tableFilterBar:popover.dateRange.startLabel',
        'Start date'
      ),
      endLabel: t(
        i18nLocale,
        'tableFilterBar:popover.dateRange.endLabel',
        'End date'
      ),
      startHigherError: t(
        i18nLocale,
        'tableFilterBar:popover.dateRange.startHigherError',
        'Start date should be before end date. Enter a valid range.'
      ),
    },
  },
  operators: {
    // String operator labels delegate to @ardoq/query-builder via the
    // locale-aware factory; the package's getOperatorsLabel resolves
    // queryBuilder:operator.* keys against the supplied I18nLocale.
    string: getDefaultTableFilterOperatorLabels(i18nLocale).string,
    date: getDefaultTableFilterOperatorLabels(i18nLocale).date,
    list: {
      is: t(i18nLocale, 'tableFilterBar:operators.list.is', 'is'),
      isAnyOf: t(
        i18nLocale,
        'tableFilterBar:operators.list.isAnyOf',
        'is any of'
      ),
      isNot: t(i18nLocale, 'tableFilterBar:operators.list.isNot', 'is not'),
    },
    boolean: {
      is: t(i18nLocale, 'tableFilterBar:operators.boolean.is', 'is'),
      isNot: t(i18nLocale, 'tableFilterBar:operators.boolean.isNot', 'is not'),
    },
  },
  dashboardPill: getDashboardPillLabels(i18nLocale),
});
